import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useShowSnackbarBarForApollo } from '@ocx-app/hooks/useShowSnackbarBarForApollo';
import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';

import { useEditProfilePopup } from '@ocx/feat-edit-profile';
import { ActivateOfferMutation, IneligibilityReason, PointsStrategy } from '@ocx/graphql';
import { canVoucherBePurchased, Offer, OfferActivationStatus, isPurchaseVoucherOffer } from '@ocx/data-offers';
import { useConfiguration } from '@ocx/data-configuration';

import { isErrorNotEnoughPointsError } from './is-not-enough-points-error.utils';
import { useRedeemOffer } from './use-redeem-offer.hook';
import { useActivateOffer } from './use-activate-offer.hook';
import { offerMessages } from './offer.messages';
import { useShowExplanationSnackbarForIneligibleOffer } from './use-show-explanation-snackbar-for-ineligible-offer.hook';
import { ineligibilityMessages } from './ineligibility.messages';

export const useRedeemOfferHelpers = ({ onCompleted }: { onCompleted?: () => unknown }) => {
  const intl = useIntl();
  const snackbar = useSnackbar();
  const showApolloErrorSnackbar = useShowSnackbarBarForApollo();
  const pointsStrategy = useConfiguration('loyaltyProgram.pointsStrategy');

  const onError = useCallback(
    (error: unknown) => {
      if (isErrorNotEnoughPointsError(error) && pointsStrategy === PointsStrategy.Points) {
        snackbar.open(
          intl.formatMessage(
            ineligibilityMessages['offer:points-strategy:points:ineligibility-reason:not-enough-funds'],
          ),
        );
        return;
      }
      if (isErrorNotEnoughPointsError(error) && pointsStrategy === PointsStrategy.Cents) {
        snackbar.open(
          intl.formatMessage(
            ineligibilityMessages['offer:points-strategy:cents:ineligibility-reason:not-enough-funds'],
          ),
        );
        return;
      }
      showApolloErrorSnackbar(error);
    },
    [intl, pointsStrategy, showApolloErrorSnackbar, snackbar],
  );

  return useRedeemOffer({
    onCompleted,
    onError,
  });
};

type UseActivateOfferHelpersParams = {
  onCompleted?: (data: ActivateOfferMutation) => unknown;
  awaitRefetchQueries?: boolean;
};

export const useActivateOfferHelpers = (params?: UseActivateOfferHelpersParams) => {
  const intl = useIntl();
  const snackbar = useSnackbar();

  const showActivateOfferError = useCallback(() => {
    snackbar.open(intl.formatMessage(offerMessages.activationFailed));
  }, [intl, snackbar]);

  return useActivateOffer({
    onCompleted: params?.onCompleted,
    onError: showActivateOfferError,
    awaitRefetchQueries: params?.awaitRefetchQueries,
  });
};

export type OfferLoyaltyActionOnClick = () => unknown;
export type OfferLoyaltyActionTitle = 'offers:activateButtonTitle' | 'offers:redeemButtonTitle';

export type OfferLoyaltyActionResult =
  | { state: 'noAction' }
  | {
      state: 'hasAction';
      onClick: OfferLoyaltyActionOnClick;
      isLoading: boolean;
      titleKey: OfferLoyaltyActionTitle;
    };

export type GetOfferLoyaltyAction = (offer: Offer) => OfferLoyaltyActionResult;

export type UseGetOfferOnLoyaltyActionClick = (params: {
  membershipIsCompleted: boolean;
  membershipPoints?: number;
  onCompleted?: () => unknown;
}) => GetOfferLoyaltyAction;
export const useGetOfferLoyaltyAction: UseGetOfferOnLoyaltyActionClick = ({
  membershipIsCompleted,
  membershipPoints = 0,
  onCompleted,
}) => {
  const intl = useIntl();

  const { activate, activating } = useActivateOfferHelpers({ onCompleted });
  const { redeem, redeeming } = useRedeemOfferHelpers({ onCompleted });

  const showIneligibilityExplanationSnackbar = useShowExplanationSnackbarForIneligibleOffer();

  const { open: openEditProfilePopup } = useEditProfilePopup();

  return useCallback(
    (offer: Offer) => {
      let actionType: OfferLoyaltyActionTitle | null = null;

      if (isPurchaseVoucherOffer(offer)) {
        actionType = 'offers:redeemButtonTitle';
      }
      if (offer.activationStatus === OfferActivationStatus.RequiresActivation) {
        actionType = 'offers:activateButtonTitle';
      }

      if (!actionType) {
        return { state: 'noAction' } as const;
      }

      if (isPurchaseVoucherOffer(offer)) {
        const purchaseEligibility = canVoucherBePurchased(offer, { membershipPoints });
        if (!purchaseEligibility.canPurchase) {
          return {
            state: 'hasAction',
            onClick: () => showIneligibilityExplanationSnackbar(purchaseEligibility.reasons[0]),
            isLoading: false,
            titleKey: actionType,
          } as const;
        }
      }

      if (offer.ineligibilityReason === IneligibilityReason.EmailRequired) {
        return {
          state: 'hasAction',
          onClick: () =>
            openEditProfilePopup({
              description: intl.formatMessage(
                ineligibilityMessages['offer:ineligibility-reason:email-required:popup-description'],
              ),
            }),
          isLoading: false,
          titleKey: actionType,
        } as const;
      }

      if (offer.activationStatus === OfferActivationStatus.RequiresActivation) {
        return {
          state: 'hasAction',
          onClick: () => {
            activate(offer);
          },
          isLoading: activating,
          titleKey: 'offers:activateButtonTitle',
        } as const;
      }

      if (isPurchaseVoucherOffer(offer)) {
        return {
          state: 'hasAction',
          onClick: () => {
            redeem(offer, { membershipIsCompleted });
          },
          isLoading: redeeming,
          titleKey: 'offers:redeemButtonTitle',
        } as const;
      }

      return { state: 'noAction' } as const;
    },
    [
      intl,
      openEditProfilePopup,
      activating,
      activate,
      redeeming,
      redeem,
      membershipIsCompleted,
      membershipPoints,
      showIneligibilityExplanationSnackbar,
    ],
  );
};
